import { defineStore } from 'pinia';
import type { dynamicObj } from 'src/pages/assets';
import { getOrgBasedKey } from 'src/utils/cacheHelper';
import {
  useAmountFormatStore,
  useAccountingTypeStore,
  useSettingStore,
  useDocumentStore,
} from 'src/store/main';
import {
  getObjectFromIndexDbWithExpiry,
  saveObjectToIndexDbWithExpiry,
  deleteObjectFromIndexDb,
} from 'src/utils/indexedDb';
import { isEmptyString, isUndefinedOrNull, objLen } from 'src/utils/general';
import { usePoMatchStore } from 'src/store/Document/po_match.store';
import { autoAccountingApiService } from 'src/services';
import { amount_rounding_setting } from 'src/constants';
import {
  auto_accounting_acc_list_name_setting,
  auto_accounting_dimensions_list_setting,
  auto_accounting_dimensions_options_setting,
  auto_accounting_po_acc_list_name_setting,
  dimension_general_dropdown_accounting,
} from 'src/constants';

export const useAutoAccountingStore = defineStore('auto_accounting', {
  state: () => ({
    accounting_mode: 'total_line',
    lineItems: [] as dynamicObj[],
    accounts_loaded: false,
    accounts: [] as dynamicObj[],
    categorized_accounts: {} as dynamicObj,
    amount_format_locale: 'en_US',
    dimensions: [] as dynamicObj[],
    dimension_dropdowns: {} as dynamicObj,
    dimension_general_dropdowns: {} as dynamicObj,
    dimension_rules: {} as dynamicObj,
    is_view_readonly: false as boolean,
    is_calculating: false,
    amount_format_store: useAmountFormatStore(),
    account_predictions: [] as dynamicObj[],
    total_line_predictions: [] as dynamicObj[],
    line_items_line_predictions: [] as dynamicObj[],
    supplier_name: '',
    accounting_error: '',
    expert_mode: false as boolean,
    amountFieldsList: [] as dynamicObj,
    taxCodeAndCountryDropdown: [] as dynamicObj,
    prediction_selections: {} as dynamicObj,
    isMultiTaxSection: false,
    accounting_type_store: useAccountingTypeStore(),
    settings_store: useSettingStore(),
    accounting_mode_options: [] as Array<dynamicObj>,
    // masterDataLimit: 99999,
    categorized_accounts_key: 'categorized_accounts',
    lnDataReqPromise: null as Promise<void> | null,
    lnPromiseResolve: null as any,
    m3DataReqPromise: null as Promise<void> | null,
    m3PromiseResolve: null as any,
    combination_by_range: {} as dynamicObj,
    max_combinations: 20,
    predictionsLoaded: false,
  }),
  getters: {
    accountFilterByAnyValue: (state: any) => (val: string) => {
      const needle = val.toLowerCase();
      let filtered = state.accounts.filter(
        (v: dynamicObj) => v.label.toLowerCase().startsWith(needle) as string,
      );

      if (filtered.length === 0) {
        filtered = state.accounts.filter(
          (v: dynamicObj) => v.label.toLowerCase().indexOf(needle) > -1,
        );
      }

      return filtered as dynamicObj[];
    },

    getTotalLineIndex: (state: any) => () => {
      let total_line_index = -1;
      state.lineItems.forEach((item: any, index: number) => {
        if (item.type === 'total_line_item') {
          total_line_index = index;
        }
      });
      return total_line_index;
    },

    predictions_by_mode() {
      const predictions = {} as dynamicObj;

      if (this.accounting_mode === 'total_line') {
        predictions['total'] = [];
        for (const [
          pred_index,
          pred_row,
        ] of this.total_line_predictions.entries()) {
          pred_row.checked = false;

          pred_row.line_index = this.lineItems.findIndex(
            (item: dynamicObj) => item.type === 'total_line_item',
          );
          predictions['total'].push(pred_row);
        }
        return predictions;
      }

      for (const [
        pred_index,
        pred_row,
      ] of this.line_items_line_predictions.entries()) {
        const key = pred_row.description;
        if (predictions[key] === undefined) {
          predictions[key] = [];
        }
        pred_row.checked = false;
        pred_row.line_index = this.lineItems.findIndex(
          (item: dynamicObj) => item.description === pred_row.description,
        );

        predictions[key].push(pred_row);
      }

      return predictions;
    },
  },
  actions: {
    async fetchDimensions() {
      try {
        this.dimensions = [] as dynamicObj[];

        const dimensions_result =
          await this.accounting_type_store.getMasterData(
            auto_accounting_dimensions_list_setting,
          );
        if (dimensions_result.length === 0) {
          return;
        }

        Array.from(dimensions_result).forEach((row: any, index: any) => {
          this.dimensions.push({
            id: row.id,
            name: row.name,
          });
        });

        this.dimensions.sort((a: dynamicObj, b: dynamicObj) => a.id - b.id);
      } catch (error) {
        throw error;
      }
    },

    getDimensionsForAccount(id: string) {
      const needle = id.toLowerCase();
      const filter_accounts = this.accounts.filter(
        (v: any) => v.value.toLowerCase().indexOf(needle) > -1,
      );

      if (filter_accounts.length == 0) {
        return [];
      }

      const account = filter_accounts[0];
      const dimensions = account.flexdimensions;
      // logInfo('getDimensionsForAccount',dimensions);
      Array.from(dimensions).forEach((row: any, index: any) => {
        row.error = '';
      });

      return JSON.parse(JSON.stringify(dimensions)) as dynamicObj[];
    },

    async sortAccounts() {
      const accounts = JSON.parse(JSON.stringify(this.accounts));
      this.categorized_accounts = this.getAccountAsCategories(accounts);

      saveObjectToIndexDbWithExpiry(
        await getOrgBasedKey(this.categorized_accounts_key),
        this.categorized_accounts,
        true,
      );
    },

    categorizedAccoutns() {
      const main_categories = {} as dynamicObj;
      for (const [index, item] of this.accounts.entries()) {
        let account_number = item.value;
        if (account_number.length === 1) {
          account_number = '0' + account_number;
        }
        if (account_number.length > 2) {
          continue;
        }

        if (!main_categories.hasOwnProperty(account_number)) {
          main_categories[account_number] = {};
        }
      }

      for (const [sub_index, item] of this.accounts.entries()) {
        let account_number = item.value;
        if (account_number.length === 1) {
          account_number = '0' + account_number;
        }

        const first_2_account_number = account_number.substring(0, 2);

        if (main_categories.hasOwnProperty(account_number)) {
          item.parent = true;
        } else {
          item.parent = false;
          item.parent_id = first_2_account_number;
        }

        if (!main_categories.hasOwnProperty(first_2_account_number)) {
          main_categories[account_number] = {};
          item.parent = true;
          item.parent_id = account_number;
          main_categories[account_number][account_number] = item;
        } else {
          main_categories[first_2_account_number][account_number] = item;
        }
      }

      return main_categories;
    },

    getAccountAsCategories(accounts: dynamicObj[]) {
      const categories = accounts;

      // Create a dictionary to store the hierarchy
      const categoryHierarchy = {} as dynamicObj;
      const linear_accounts = {} as dynamicObj;

      for (const [index, item] of accounts.entries()) {
        const categoryId = item.id;
        categoryHierarchy[categoryId] = {};
        linear_accounts[categoryId] = item;
        const filteredCategories = categories.filter((category) => {
          const category_id = category.id;
          return (
            category_id.startsWith(categoryId) && category.id !== categoryId
          );
        });

        for (const [index, filtered_item] of filteredCategories.entries()) {
          categoryHierarchy[categoryId][filtered_item.id] = filtered_item.id;
        }
      }

      // step 1
      for (const main_category_name of Object.keys(categoryHierarchy)) {
        const categories = JSON.parse(
          JSON.stringify(categoryHierarchy[main_category_name]),
        );
        for (const sub_category_name of Object.keys(categories)) {
          const sub_categories = categoryHierarchy[sub_category_name];
          if (Object.keys(sub_categories).length > 0) {
            for (const child_name of Object.keys(sub_categories)) {
              delete categoryHierarchy[main_category_name][child_name];
            }
          }
        }
      }

      // step 2
      const delete_keys = [] as string[];
      for (const main_category_name of Object.keys(categoryHierarchy)) {
        const categories = JSON.parse(
          JSON.stringify(categoryHierarchy[main_category_name]),
        );
        for (const sub_category_name of Object.keys(categories)) {
          const sub_categories = categoryHierarchy[sub_category_name];
          categoryHierarchy[main_category_name][sub_category_name] =
            sub_categories;
          delete_keys.push(sub_category_name);
        }
      }

      // step 3
      for (const [index, item] of delete_keys.entries()) {
        delete categoryHierarchy[item];
      }

      const result = this.makeCategories(linear_accounts, categoryHierarchy);

      return result as dynamicObj;
    },

    makeCategories(linear_accounts: dynamicObj, categoryHierarchy: dynamicObj) {
      const result = [] as dynamicObj[];
      if (Object.keys(categoryHierarchy).length === 0) {
        return result;
      }

      for (const main_category_name of Object.keys(categoryHierarchy)) {
        const node = linear_accounts[main_category_name] as dynamicObj;
        const childern = this.makeCategories(
          linear_accounts,
          categoryHierarchy[main_category_name],
        );

        node.children = [] as dynamicObj[];
        for (const [index, item] of Object.keys(childern).entries()) {
          node.children.push(childern[item as any]);
        }
        result.push(node);
      }

      return result;
    },

    async deleteLineItem(index: number) {
      try {
        this.lineItems.splice(index, 1);
        await this.calculateTotalBylineItems();
      } catch (err) {
        logError(err);
      }
    },

    async calculateTotalByMode() {
      try {
        if (this.is_calculating) {
          return;
        }
        this.is_calculating = true;
        const total_line_index = this.getTotalLineIndex();
        if (total_line_index !== -1) {
          this.lineItems[total_line_index].error = '';
        }

        if (!this.isMultiTaxSection && this.accounting_mode === 'total_line') {
          await this.calculateTotalByTotalSplittedLines();
        } else {
          await this.calculateTotalBylineItems();
        }
      } catch (err) {
      } finally {
        this.is_calculating = false;
      }
    },

    async checkTotalForEachLineItem(line_item: dynamicObj) {
      let calculated_total_by_lines = 0;
      let total_line_amount = line_item.amount;

      if (line_item.split_items.length === 0) {
        return true;
      }
      const values = [];
      values.push({
        key: total_line_amount,
        value: total_line_amount,
      });

      for (const [index, item] of line_item.split_items.entries()) {
        if (item.amount === '') {
          continue;
        }
        values.push({
          key: item.amount,
          value: item.amount,
        });
      }

      const formatted_values = await this.amount_format_store.formatMultiValues(
        values,
        this.amount_format_locale,
      );

      for (const [index, item] of line_item.split_items.entries()) {
        if (item.amount === '') {
          continue;
        }
        const amount_formatted = this.searchValueByKey(
          item.amount,
          formatted_values,
        );
        const amount_standard = parseFloat(amount_formatted);
        calculated_total_by_lines += amount_standard;
      }

      const total_line_amount_formatted = this.searchValueByKey(
        line_item.amount,
        formatted_values,
      );
      total_line_amount = parseFloat(total_line_amount_formatted);
      const remaining_amount =
        +total_line_amount.toFixed(4) - +calculated_total_by_lines.toFixed(4);
      return remaining_amount === 0;
    },

    // TODO need to change this method
    async calculateTotalByTotalSplittedLines() {
      let calculated_total_by_lines = 0;
      let total_line_amount = 0;

      const total_line_index = this.getTotalLineIndex();
      if (total_line_index === -1) {
        return;
      }

      const total_line_item = this.lineItems[total_line_index];
      if (total_line_item.split_items.length == 0) {
        this.lineItems[total_line_index].lines_total_amount =
          total_line_item.amount;
        this.lineItems[total_line_index].pending_amount =
          await this.amount_format_store.formatValue(
            '0.00',
            'en_US',
            this.amount_format_locale,
          );

        return;
      }

      const values = [];
      values.push({
        key: total_line_item.amount,
        value: total_line_item.amount,
      });

      for (const [index, item] of total_line_item.split_items.entries()) {
        if (item.amount === '') {
          continue;
        }
        values.push({
          key: item.amount,
          value: item.amount,
        });
      }

      if (values.length === 0) {
        return;
      }

      const formatted_values = await this.amount_format_store.formatMultiValues(
        values,
        this.amount_format_locale,
      );

      for (const [index, item] of total_line_item.split_items.entries()) {
        if (item.amount === '') {
          continue;
        }
        const amount_formatted = this.searchValueByKey(
          item.amount,
          formatted_values,
        );
        const amount_standard = parseFloat(amount_formatted);
        item.amount_standard = amount_standard;
        calculated_total_by_lines += amount_standard;
      }

      const total_line_amount_formatted = this.searchValueByKey(
        total_line_item.amount,
        formatted_values,
      );
      total_line_amount = parseFloat(total_line_amount_formatted);
      total_line_item.amount_standard = total_line_amount;

      await this.calculateRemainingAmount(
        total_line_index,
        total_line_amount,
        calculated_total_by_lines,
      );
    },

    async calculateTotalBylineItems() {
      let calculated_total_by_lines = 0;
      let total_line_amount = 0;
      const values = [];

      const total_line_index = this.getTotalLineIndex();
      if (total_line_index === -1) {
        return;
      }

      if (this.lineItems.length > 0) {
        for (const [index, item] of this.lineItems.entries()) {
          if (item.amount === '') {
            continue;
          }
          values.push({
            key: item.amount,
            value: item.amount,
          });

          for (const [index, splitted_item] of item.split_items.entries()) {
            if (splitted_item.amount === '') {
              continue;
            }
            values.push({
              key: splitted_item.amount,
              value: splitted_item.amount,
            });
          }
        }
      }

      if (values.length > 0) {
        const formatted_values =
          await this.amount_format_store.formatMultiValues(
            values,
            this.amount_format_locale,
          );

        for (const [index, item] of this.lineItems.entries()) {
          if (!this.isMultiTaxSection) {
            if (item.amount === '' || item.type === 'total_line_item') {
              continue;
            }
          }

          const amount_formatted = this.searchValueByKey(
            item.amount,
            formatted_values,
          );
          const item_amount_float = parseFloat(amount_formatted);
          item.amount_standard = item_amount_float;
          calculated_total_by_lines += item_amount_float;

          if (item.split_items.length == 0) {
            item.pending_amount = await this.amount_format_store.formatValue(
              '0.00',
              'en_US',
              this.amount_format_locale,
            );
          } else {
            let calculated_total_by_splitted_lines = 0;
            for (const [index, splitted_item] of item.split_items.entries()) {
              if (splitted_item.amount === '') {
                continue;
              }
              const amount_formatted = this.searchValueByKey(
                splitted_item.amount,
                formatted_values,
              );
              const amount_standard = parseFloat(amount_formatted);
              splitted_item.amount_standard = amount_standard;
              calculated_total_by_splitted_lines += amount_standard;
            }

            const item_remaining_amount =
              item_amount_float - calculated_total_by_splitted_lines;

            let item_remaining_amount_fixed: any =
              item_remaining_amount.toFixed(2);
            item_remaining_amount_fixed = await this.roundingDiff(
              item_remaining_amount_fixed,
            );

            const is_negative = item_remaining_amount < 0;
            item.pending_amount =
              (is_negative ? '-' : '') +
              (await this.amount_format_store.formatValue(
                item_remaining_amount_fixed,
                'en_US',
                this.amount_format_locale,
              ));
          }
        }

        if (
          !this.isMultiTaxSection &&
          this.lineItems[total_line_index].amount !== ''
        ) {
          const total_line_amount_formatted = this.searchValueByKey(
            this.lineItems[total_line_index].amount,
            formatted_values,
          );
          total_line_amount = parseFloat(total_line_amount_formatted);
          this.lineItems[total_line_index].amount_standard = total_line_amount;
        }
      }
      if (!this.isMultiTaxSection) {
        await this.calculateRemainingAmount(
          total_line_index,
          total_line_amount,
          calculated_total_by_lines,
        );
      }
    },

    areAlmostEqual(amount: string, tolerance = 0.01) {
      return Math.abs(Number(amount)) <= tolerance;
    },

    async roundingDiff(amount: string) {
      const settings_store = useSettingStore();
      await settings_store.startSettingsFetch();
      const { user_settings } = settings_store;

      if (
        user_settings[amount_rounding_setting] &&
        user_settings[amount_rounding_setting] === true &&
        this.areAlmostEqual(amount)
      ) {
        return 0;
      }

      return amount;
    },

    async calculateRemainingAmount(
      total_line_index: number,
      total_line_amount: number,
      calculated_total_by_lines: number,
    ) {
      const remaining_amount = total_line_amount - calculated_total_by_lines;
      let remaining_amount_fixed: any = remaining_amount.toFixed(2);
      remaining_amount_fixed = await this.roundingDiff(remaining_amount_fixed);
      if (remaining_amount_fixed === '-0.00') {
        remaining_amount_fixed = '0.00';
      }

      let is_negative =
        remaining_amount_fixed === '0.00' ? false : remaining_amount < 0;
      const calculated_total_by_lines_fixed =
        calculated_total_by_lines.toFixed(2);

      const values = [];
      values.push({
        key: remaining_amount_fixed,
        value: remaining_amount_fixed,
      });

      values.push({
        key: calculated_total_by_lines_fixed,
        value: calculated_total_by_lines_fixed,
      });

      if (
        total_line_index === -1 ||
        typeof this.lineItems[total_line_index] === 'undefined'
      ) {
        return;
      }

      const formatted_values = await this.amount_format_store.formatMultiValues(
        values,
        'en_US',
        this.amount_format_locale,
      );

      if (remaining_amount_fixed == '0') {
        is_negative = false;
      }

      this.lineItems[total_line_index].pending_amount =
        (is_negative ? '-' : '') +
        this.searchValueByKey(remaining_amount_fixed, formatted_values);

      this.lineItems[total_line_index].lines_total_amount =
        this.searchValueByKey(
          calculated_total_by_lines_fixed,
          formatted_values,
        );
    },

    updateAccount(
      index: number,
      child_index: number | null,
      account: dynamicObj,
    ) {
      // splitted line item
      if (child_index !== null) {
        this.lineItems[index].split_items[child_index].account = account;
        this.lineItems[index].split_items[child_index].account_error = '';

        if (!account || objLen(account) === 0) {
          this.lineItems[index].split_items[child_index].dimensions = [];
          return;
        }

        this.lineItems[index].split_items[child_index].dimensions =
          this.getDimensionsForAccount(account.value);
        return;
      }

      // main line item
      this.lineItems[index].account = account;
      this.lineItems[index].account_error = '';
      if (!account || objLen(account) === 0) {
        this.lineItems[index].dimensions = [];
        return;
      }

      this.lineItems[index].dimensions = this.getDimensionsForAccount(
        account.value,
      );
    },

    baseSplitLineItem(): any {
      return this.accounting_type_store.baseSplitLineItem();
    },

    resetAccountingStore() {
      this.amount_format_store.resetStore();
      this.lineItems = [] as dynamicObj[];
      this.accounts_loaded = false;
      this.lnDataReqPromise = null;
      this.lnPromiseResolve = null;
      this.m3DataReqPromise = null;
      this.m3PromiseResolve = null;
      this.accounts = [] as dynamicObj[];
      this.categorized_accounts = {} as dynamicObj;
      this.dimensions = [] as dynamicObj[];
      this.dimension_dropdowns = {} as dynamicObj;
      this.is_view_readonly = false;
      this.account_predictions = [] as dynamicObj[];
      this.total_line_predictions = [] as dynamicObj[];
      this.line_items_line_predictions = [] as dynamicObj[];
      this.supplier_name = '';
      this.prediction_selections = {} as dynamicObj;
      this.expert_mode = false;
      this.accounting_mode = 'total_line';
    },

    searchValueByKey(key: string, arrayOfObjects: dynamicObj[]) {
      const foundObject = arrayOfObjects.find((obj) => obj.key === key);
      if (!foundObject) {
        return '';
      }

      return foundObject.value as string;
    },

    async autFillEmptyLineItems(index: number) {
      const empty_amount_items = this.lineItems[index].split_items.filter(
        (item: dynamicObj) => item.amount === '',
      );
      if (empty_amount_items.length === 0) {
        this.calculateTotalByMode();
        return;
      }

      // check if percentage is 100 percent
      const total_used_percent = await this.getTotalUsedPercent(index);

      const total_used_amount = await this.getTotalUsedAmount(index);
      if (total_used_percent === 100) {
        this.calculateTotalByMode();
        return;
      }

      const line_item_amount_standard = this.lineItems[index].amount_standard;

      const remaining = line_item_amount_standard - total_used_amount;
      const equal_amount = remaining / empty_amount_items.length;
      const amount_standard = parseFloat(equal_amount.toFixed(2));
      const percentage = (equal_amount / line_item_amount_standard) * 100;
      const equal_percentage_float = parseFloat(percentage.toFixed(2));

      const percent_string = String(equal_percentage_float);
      const amount_float_string = String(amount_standard);

      const values = [];
      values.push({
        key: amount_float_string,
        value: amount_float_string,
      });

      values.push({
        key: percent_string,
        value: percent_string,
      });

      const formatted_values = await this.amount_format_store.formatMultiValues(
        values,
        'en_US',
        this.amount_format_locale,
      );

      const percent_formatted = this.searchValueByKey(
        percent_string,
        formatted_values,
      );
      const amount_formatted = this.searchValueByKey(
        amount_float_string,
        formatted_values,
      );

      for (const [item_index, item] of this.lineItems[
        index
      ].split_items.entries()) {
        if (item.amount === '') {
          item.percentage = percent_formatted + '%';
          item.percent_float = equal_percentage_float;

          item.amount = amount_formatted;
          item.amount_standard = amount_standard;
        }
      }

      // calculate total by line items
      this.calculateTotalByMode();
    },

    async getTotalUsedAmount(index: number) {
      let total_used_amount = 0;
      const values = [];

      for (const [item_index, item] of this.lineItems[
        index
      ].split_items.entries()) {
        if (!item.hasOwnProperty('amount_standard') && item.amount !== '') {
          values.push({
            key: item.amount,
            value: item.amount,
          });
        }

        // add to total_used_percent if item has percent_float and amount
        if (item.hasOwnProperty('amount_standard') && item.amount !== '') {
          total_used_amount += item.amount_standard;
        }
      }

      if (values.length > 0) {
        const formatted_values =
          await this.amount_format_store.formatMultiValues(
            values,
            this.amount_format_locale,
          );

        // add missig percent_float for items that has amount
        for (const [item_index, item] of this.lineItems[
          index
        ].split_items.entries()) {
          if (!item.hasOwnProperty('amount_standard') && item.amount !== '') {
            const amount_formatted = this.searchValueByKey(
              item.amount,
              formatted_values,
            );
            item.amount_standard = parseFloat(amount_formatted);
            total_used_amount += item.amount_standard;
          }
        }
      }

      return total_used_amount;
    },

    async getTotalUsedPercent(index: number) {
      let total_used_percent = 0;
      const values = [];

      for (const [item_index, item] of this.lineItems[
        index
      ].split_items.entries()) {
        if (!item.hasOwnProperty('percent_float') && item.amount !== '') {
          values.push({
            key: item.percentage,
            value: item.percentage,
          });
        }

        // add to total_used_percent if item has percent_float and amount
        if (item.hasOwnProperty('percent_float') && item.amount !== '') {
          total_used_percent += item.percent_float;
        }
      }

      if (values.length > 0) {
        const formatted_values =
          await this.amount_format_store.formatMultiValues(
            values,
            this.amount_format_locale,
          );

        // add missig percent_float for items that has amount
        for (const [item_index, item] of this.lineItems[
          index
        ].split_items.entries()) {
          if (!item.hasOwnProperty('percent_float') && item.amount !== '') {
            const percent_formatted = this.searchValueByKey(
              item.percentage,
              formatted_values,
            );
            item.percent_float = parseFloat(percent_formatted);
            total_used_percent += item.percent_float;
          }
        }
      }

      return total_used_percent;
    },

    validateLine(index: number, child_index: null | number = null) {
      if (child_index !== null) {
        this.lineItems[index].split_items[child_index].is_validated = true;
      }
      this.lineItems[index].is_validated = true;
    },

    hasTotalPredictions() {
      return (
        this.accounting_mode === 'total_line' &&
        this.total_line_predictions.length > 0
      );
    },

    hasLineItemsPredictions() {
      return (
        this.accounting_mode === 'line_items' &&
        this.line_items_line_predictions.length > 0
      );
    },

    resetPredictionData() {
      this.total_line_predictions = [] as dynamicObj[];
      this.line_items_line_predictions = [] as dynamicObj[];
      this.account_predictions = [] as dynamicObj[];
    },

    async getPredictions(data: dynamicObj) {
      this.resetPredictionData();
      const response =
        await autoAccountingApiService.getAccountPredictions(data);
      if (response.success === false) {
        throw { message: response.message };
      }

      if (!response.data || response.data.length === 0) {
        return;
      }

      for (const [index, row] of response.data.entries()) {
        const item = row as dynamicObj;
        this.account_predictions.push(item);
      }
      this.filterUsablePredictedAccount();
    },

    filterUsablePredictedAccount() {
      const filteredPredictions = this.account_predictions
        .filter((item: dynamicObj) => item.ledger_account !== '') // Filter out items with empty ledger_account
        .reduce((uniqueItems: dynamicObj[], currentItem: dynamicObj) => {
          // Check if currentItem's ledger_account is already in uniqueItems
          const existingItem = uniqueItems.find(
            (item: dynamicObj) =>
              item.ledger_account === currentItem.ledger_account,
          );

          // If not found, add currentItem to uniqueItems
          if (!existingItem) {
            uniqueItems.push(currentItem);
          }

          return uniqueItems;
        }, []);

      for (const [index, row] of filteredPredictions.entries()) {
        for (const [index, item] of row.predictions.entries()) {
          const predicted_account = this.accounts.find(
            (account: any) => account.id === item['ledger_account'],
          );
          if (!predicted_account) {
            continue;
          }

          item.account_obj = predicted_account;
          item.percentage = (item.probability * 100).toFixed(0);
          item.percentage = item.percentage.replace(/\.0+$/, '');

          if (!row.hasOwnProperty('description')) {
            item.description = '';
            this.total_line_predictions.push(item);
          } else {
            item.description = row.description;
            this.line_items_line_predictions.push(item);
          }
        }
      }
    },

    clearPredictions() {
      this.account_predictions = [] as dynamicObj[];
      this.total_line_predictions = [] as dynamicObj[];
      this.line_items_line_predictions = [] as dynamicObj[];
    },

    getLineItemsForPredictionAccount() {
      const line_items = [] as Array<dynamicObj>;

      const total_line_index = this.getTotalLineIndex();
      if (total_line_index === -1) {
        return line_items;
      }

      for (const [index, item] of this.lineItems.entries()) {
        if (item.type === 'total_line_item') {
          const line = {} as dynamicObj;
          line.supplier_name = this.supplier_name;
          line_items.push(line);
          continue;
        }

        if (!item.description || item.description === '') {
          continue;
        }

        const line = {} as dynamicObj;
        line.supplier_name = this.supplier_name;
        line.description = item.description;
        line_items.push(line);
      }

      return line_items;
    },

    resetItem(index: number) {
      this.lineItems[index].account = '';
      this.lineItems[index].account_error = '';
      this.lineItems[index].booking_reference = '';
      this.lineItems[index].dimensions = [];
      this.lineItems[index].checked = false;

      const { view_template, view_types } = this.accounting_type_store;
      if (view_template === view_types.m3) {
        this.lineItems[index].voucher_text = '';
      }

      const item = this.accounting_type_store.resetlineAdditionalFields(
        this.lineItems[index],
      );
      this.lineItems[index] = item;
    },

    // auto complete
    highlightOption(value: string, input_value: string) {
      if (
        isUndefinedOrNull(value) ||
        isEmptyString(value) ||
        isEmptyString(input_value) ||
        isUndefinedOrNull(input_value) ||
        typeof value !== 'string' ||
        typeof input_value !== 'string'
      ) {
        return value;
      }

      const cleaned_value = value.trim();
      const cleaned_input_value = input_value.trim();

      const value_lower = cleaned_value.toLowerCase();
      const input_value_lower = cleaned_input_value.toLowerCase();
      const searchIndex = value_lower.indexOf(input_value_lower);

      if (
        searchIndex === -1 ||
        cleaned_input_value.length > cleaned_value.length
      ) {
        return value;
      }

      const highlightedPart = cleaned_value.substring(
        searchIndex,
        searchIndex + cleaned_input_value.length,
      );
      const beforePart = cleaned_value.substring(0, searchIndex);
      const afterPart = cleaned_value.substring(
        searchIndex + cleaned_input_value.length,
      );
      const label = `${beforePart}<strong>${highlightedPart}</strong>${afterPart}`;
      return label;
    },

    isValidData() {
      const doc_store = useDocumentStore();
      const has_supplier =
        !isUndefinedOrNull(this.supplier_name) &&
        !isEmptyString(this.supplier_name);

      if (doc_store.isElectronicDocument() && !has_supplier) {
        return true;
      }

      if (!has_supplier) {
        return false;
      }

      return true;
    },

    async reloadLnAccounts() {
      this.lnDataReqPromise = null;
      await this.fetchLnAccountsPromise(false);
    },

    async fetchLnAccountsPromise(loadFromCache = true) {
      if (this.lnDataReqPromise) {
         
        return this.lnDataReqPromise;
      }

      this.lnDataReqPromise = new Promise((resolve) => {
        this.lnPromiseResolve = resolve;
        this.getAccountsListWithAsync(loadFromCache);
      });

      return this.lnDataReqPromise;
    },

    // preapre LN data
    async getAccountsListWithAsync(loadFromCache = true) {
      try {
        this.accounts_loaded = false;
        this.accounts = [] as dynamicObj[];
        this.dimensions = [] as dynamicObj[];
        this.dimension_dropdowns = {} as dynamicObj;

        let isDataExpired = false;

        if (loadFromCache) {
          isDataExpired = await this.setDataFromCache();
        }

        if (!isDataExpired && this.accounts_loaded) {
          if (this.lnPromiseResolve) {
            this.lnPromiseResolve();
          }
          return;
        }

        const acc_list = this.accounting_type_store.prepareRequestData(auto_accounting_acc_list_name_setting,true);
        const dimensions_list = this.accounting_type_store.prepareRequestData(auto_accounting_dimensions_list_setting,true);
        const dimensions_options =this.accounting_type_store.prepareRequestData(auto_accounting_dimensions_options_setting,true);

        // Map of api name -> its setting name, used to re-build paginated requests
        const settingNameByApi: dynamicObj = {
          dimensions_list: auto_accounting_dimensions_list_setting,
          acc_list: auto_accounting_acc_list_name_setting,
          dimensions_options: auto_accounting_dimensions_options_setting,
        };

        // Do not change the api order
        const apisParamsList = [] as any;

        if (dimensions_list) {
          apisParamsList.push({
            params: dimensions_list,
            name: 'dimensions_list',
          });
        }

        if (acc_list) {
          apisParamsList.push({ params: acc_list, name: 'acc_list' });
        }

        if (dimensions_options) {
          apisParamsList.push({
            params: dimensions_options,
            name: 'dimensions_options',
          });
        }

        if (apisParamsList.length == 0) {
          return;
        }

        // Loads all pages for every list (combining records) before processing,
        // with retry-on-failure for any failed request.
        const accountingData = await this.accounting_type_store.getAccountingDropdownsPaginated(
            apisParamsList,
            settingNameByApi,
          );

        if (!accountingData || accountingData.length == 0) {
          return;
        }

        for (const item of accountingData) {
          if (!item.data || item.data.length == 0) {
            continue;
          }

          if (item.name == 'dimensions_list') {
            await this.prepareDimensionsListData(item.data);
          } else if (item.name == 'acc_list') {
            await this.prepareAccountsData(item.data);
          } else if (item.name == 'dimensions_options') {
            await this.prepareDimensionsOptionsData(item.data);
          }
        }

        await this.sortAccounts();
        this.accounts_loaded = true;
      } catch (error) {
        throw error;
      } finally {
        if (this.lnPromiseResolve) {
          this.lnPromiseResolve();
        }
      }
    },

    async prepareAccountsData(accounts_result: any) {
      this.accounts = [] as dynamicObj[];

      if (!accounts_result || accounts_result.length == 0) {
        deleteObjectFromIndexDb(
          await getOrgBasedKey(
            this.accounting_type_store.getSettingsName(
              auto_accounting_acc_list_name_setting,
            ),
          ),
        );
        return;
      }

      this.prepareAccountsDropdown(accounts_result);

      saveObjectToIndexDbWithExpiry(
        await getOrgBasedKey(
          this.accounting_type_store.getSettingsName(
            auto_accounting_acc_list_name_setting,
          ),
        ),
        accounts_result,
        true,
      );
    },
    prepareAccountsDropdown(accounts_result: any) {
      for (const [index, row] of accounts_result.entries()) {
        if (!row.nominalaccount) {
          continue;
        }

        const flexdimensionsArray = [] as dynamicObj[];

        if (row.flexdimensions) {
          // Take the first 12
          const first12Dimensions = row.flexdimensions.substring(0, 12);
          Array.from(first12Dimensions).forEach((row: any, rowIndex: any) => {
            if (this.dimensions[rowIndex]) {
              flexdimensionsArray.push({
                rule: row,
                name: this.dimensions[rowIndex].name,
                id: this.dimensions[rowIndex].id,
                value: '',
                error: '',
              });
            }
          });
        }

        const label =
          row.nominalaccount + (row.description ? ' - ' + row.description : '');
        this.accounts.push({
          value: row.nominalaccount,
          id: row.nominalaccount,
          flexdimensions: flexdimensionsArray,
          label: label,
        });
      }
    },

    async prepareDimensionsListData(dimensionsData: any) {
      this.dimensions = [] as dynamicObj[];

      if (dimensionsData.length === 0) {
        deleteObjectFromIndexDb(
          await getOrgBasedKey(
            this.accounting_type_store.getSettingsName(
              auto_accounting_dimensions_list_setting,
            ),
          ),
        );
        return;
      }

      this.prepareDimensionsDropdown(dimensionsData);

      saveObjectToIndexDbWithExpiry(
        await getOrgBasedKey(
          this.accounting_type_store.getSettingsName(
            auto_accounting_dimensions_list_setting,
          ),
        ),
        dimensionsData,
        true,
      );
    },

    prepareDimensionsDropdown(dimensionsData: any) {
      Array.from(dimensionsData).forEach((row: any, index: any) => {
        this.dimensions.push({
          id: row.id,
          name: row.name,
        });
      });

      this.dimensions.sort((a: dynamicObj, b: dynamicObj) => a.id - b.id);
    },

    async prepareDimensionsOptionsData(dimensionsData: any) {
      this.dimension_dropdowns = {} as dynamicObj;

      if (!dimensionsData || dimensionsData.length == 0) {
        deleteObjectFromIndexDb(
          await getOrgBasedKey(
            this.accounting_type_store.getSettingsName(
              auto_accounting_dimensions_options_setting,
            ),
          ),
        );
        return;
      }

      this.prepareDimensionsOptionsDropdown(dimensionsData);

      saveObjectToIndexDbWithExpiry(
        await getOrgBasedKey(
          this.accounting_type_store.getSettingsName(
            auto_accounting_dimensions_options_setting,
          ),
        ),
        dimensionsData,
        true,
      );
    },
    prepareDimensionsOptionsDropdown(dimensionsData: any) {
      const data_by_dimensionid = {} as dynamicObj;
      for (const [index, row] of dimensionsData.entries()) {
        const dimensionid = String(row.dimensionid);
        if (!dimensionid || !row.id || !row.name) {
          continue;
        }

        if (!data_by_dimensionid.hasOwnProperty(dimensionid)) {
          data_by_dimensionid[dimensionid] = [] as dynamicObj[];
        }

        data_by_dimensionid[dimensionid].push({
          value: row.codevalue ? row.codevalue : row.id,
          label: row.id + ' - ' + row.name,
        });
      }

      for (const key in data_by_dimensionid) {
        const sortedArray = data_by_dimensionid[key].sort((a: any, b: any) => {
          return a.label.localeCompare(b.label) as number;
        });

        this.dimension_dropdowns[String(key)] = [...sortedArray];
      }
    },
    // fill the data from cache
    async setDataFromCache() {
      let isDataExpired = false;

      const dimensions_list = await this.getCacheData(
        this.accounting_type_store.getSettingsName(
          auto_accounting_dimensions_list_setting,
        ),
      );
      if (dimensions_list != null && Object.keys(dimensions_list).length > 0) {
        this.prepareDimensionsDropdown(dimensions_list.value);

        if (dimensions_list.isDataExpired) {
          isDataExpired = true;
        }
      }

      const acc_list = await getObjectFromIndexDbWithExpiry(
        await getOrgBasedKey(
          this.accounting_type_store.getSettingsName(
            auto_accounting_acc_list_name_setting,
          ),
        ),
        true,
        true,
      );
      if (acc_list != null && Object.keys(acc_list).length > 0) {
        this.prepareAccountsDropdown(acc_list.value);

        if (acc_list.isDataExpired) {
          isDataExpired = true;
        }
      }

      const dimensions_options = await this.getCacheData(
        this.accounting_type_store.getSettingsName(
          auto_accounting_dimensions_options_setting,
        ),
      );
      if (
        dimensions_options != null &&
        Object.keys(dimensions_options).length > 0
      ) {
        this.prepareDimensionsOptionsDropdown(dimensions_options.value);

        if (dimensions_options.isDataExpired) {
          isDataExpired = true;
        }
      }

      const categorizedData = await this.getCacheData(
        this.categorized_accounts_key,
      );
      if (categorizedData != null && Object.keys(categorizedData).length > 0) {
        this.categorized_accounts = categorizedData.value;

        if (categorizedData.isDataExpired) {
          isDataExpired = true;
        }
      }

      if (
        this.accounts != null &&
        this.accounts.length > 0 &&
        this.dimensions != null &&
        this.dimensions.length > 0 &&
        this.dimension_dropdowns != null &&
        Object.keys(this.dimension_dropdowns).length > 0
      ) {
        this.accounts_loaded = true;
      }

      return isDataExpired;
    },

    // ───────────────────────── M3 accounting data ─────────────────────────
    // Centralised M3 loading/caching, mirroring the LN flow above. The M3
    // template (M3.vue) is a thin consumer of these actions.

    hasM3PoMatchData() {
      return usePoMatchStore().hasPoMatchData();
    },

    async reloadM3Accounts() {
      this.m3DataReqPromise = null;
      await this.fetchM3AccountsPromise(false);
    },

    async fetchM3AccountsPromise(loadFromCache = true) {
      if (this.m3DataReqPromise) {
        return this.m3DataReqPromise;
      }

      this.m3DataReqPromise = new Promise((resolve) => {
        this.m3PromiseResolve = resolve;
        this.getM3AccountsListWithAsync(loadFromCache);
      });

      return this.m3DataReqPromise;
    },

    // prepare M3 data
    async getM3AccountsListWithAsync(loadFromCache = true) {
      try {
        this.accounts_loaded = false;
        this.accounts = [] as dynamicObj[];
        this.dimensions = [] as dynamicObj[];
        this.dimension_dropdowns = {} as dynamicObj;

        let isDataExpired = false;

        if (loadFromCache) {
          isDataExpired = await this.setM3DataFromCache();
        }

        if (!isDataExpired && this.accounts_loaded) {
          if (this.m3PromiseResolve) {
            this.m3PromiseResolve();
          }
          return;
        }

        const accSettingName = this.hasM3PoMatchData() ? auto_accounting_po_acc_list_name_setting : auto_accounting_acc_list_name_setting;

        // Map of api name -> its setting name, used to re-build paginated requests
        const settingNameByApi: dynamicObj = {
          dimension_rules_data: auto_accounting_dimensions_options_setting,
          acc_list: accSettingName,
          general_dropdown: dimension_general_dropdown_accounting,
        };

        const acc_list = this.accounting_type_store.prepareRequestData(accSettingName, true);
        const general_dropdown = this.accounting_type_store.prepareRequestData(dimension_general_dropdown_accounting, true);
        const dimension_rules_data = this.accounting_type_store.prepareRequestData(auto_accounting_dimensions_options_setting, true);

        // Do not change the api order
        const apisParamsList = [] as any;

        if (dimension_rules_data) {
          apisParamsList.push({ params: dimension_rules_data, name: 'dimension_rules_data' });
        }

        if (acc_list) {
          apisParamsList.push({ params: acc_list, name: 'acc_list' });
        }

        if (general_dropdown) {
          apisParamsList.push({ params: general_dropdown, name: 'general_dropdown' });
        }

        if (apisParamsList.length == 0) {
          return;
        }

        // Loads all pages for every list (combining records) before processing.
        const accountingData = await this.accounting_type_store.getAccountingDropdownsPaginated(apisParamsList, settingNameByApi);

        if (!accountingData || accountingData.length == 0) {
          return;
        }

        for (const item of accountingData) {
          if (!item.data || item.data.length == 0) {
            continue;
          }

          if (item.name == 'dimension_rules_data') {
            await this.prepareM3DimensionRulesData(item.data);
          } else if (item.name == 'acc_list') {
            await this.prepareM3AccountsData(item.data);
          } else if (item.name == 'general_dropdown') {
            await this.prepareM3DimensionsOptionsData(item.data);
          }
        }

        this.accounts_loaded = true;
      } catch (error) {
        throw error;
      } finally {
        if (this.m3PromiseResolve) {
          this.m3PromiseResolve();
        }
      }
    },

    // Fetch only the given acc-list key from the server and populate accounts.
    // Only does so when the OTHER acc-list key is already cached (e.g. the
    // PO-match state flipped, so data was previously cached under the adjacent
    // key); otherwise there's nothing to reconcile and it returns early.
    async loadM3AccountsFromServer(accSettingName: string) {
      const adjacentSettingName = this.hasM3PoMatchData() ? auto_accounting_acc_list_name_setting : auto_accounting_po_acc_list_name_setting;

      const adjacent_acc_list = await getObjectFromIndexDbWithExpiry(
        await getOrgBasedKey(this.accounting_type_store.getSettingsName(adjacentSettingName)),
        true,
        true,
      );

      if (!(adjacent_acc_list != null && Object.keys(adjacent_acc_list).length > 0)) {
        return;
      }

      const acc_list_params = this.accounting_type_store.prepareRequestData(accSettingName, true);
      if (!acc_list_params) {
        return;
      }

      const accountingData = await this.accounting_type_store.getAccountingDropdownsPaginated(
        [{ params: acc_list_params, name: 'acc_list' }],
        { acc_list: accSettingName },
      );

      const acc_item = accountingData?.find(
        (item: dynamicObj) => item.name === 'acc_list',
      );
      if (acc_item && acc_item.data && acc_item.data.length > 0) {
        await this.prepareM3AccountsData(acc_item.data);
      }
    },

    async prepareM3AccountsData(accounts_result: any) {
      this.accounts = [] as dynamicObj[];
      const accSettingName = this.hasM3PoMatchData() ? auto_accounting_po_acc_list_name_setting : auto_accounting_acc_list_name_setting;

      if (!accounts_result || accounts_result.length == 0) {
        deleteObjectFromIndexDb(
          await getOrgBasedKey(this.accounting_type_store.getSettingsName(accSettingName)),
        );
        return;
      }

      this.prepareM3AccountsAndDimensions(accounts_result);

      saveObjectToIndexDbWithExpiry(
        await getOrgBasedKey(this.accounting_type_store.getSettingsName(accSettingName)),
        accounts_result,
        true,
      );
    },

    prepareM3AccountsAndDimensions(accounts_result: any) {
      for (const [index, row] of accounts_result.entries()) {
        if (!row.id) {
          continue;
        }

        const dismensions = this.getM3Dimensions(row);
        const label = row.id + (row.description ? ' - ' + row.description : '');
        this.accounts.push({
          value: row.id,
          id: row.id,
          flexdimensions: dismensions,
          label: label,
        });
      }

      this.generateM3DimensionDropdowns();
    },

    getM3Dimensions(account: dynamicObj) {
      const excludeAttributes = ['dimension1', 'dimensionprofile'];
      this.dimensions = [] as dynamicObj[];
      const dimensions = [] as dynamicObj[];
      // m required, o optional
      for (const [key, value] of Object.entries(account)) {
        if (excludeAttributes.includes(key)) {
          continue;
        }

        if (key.startsWith('dimension')) {
          const dimensionId = key.split('dimension')[1];

          let rule = 'o';
          const rule_key = 'usage' + dimensionId;
          if (
            account.hasOwnProperty(rule_key) &&
            String(account[rule_key]).toLowerCase() === 'mandatory'
          ) {
            rule = 'm';
          }

          this.dimensions.push({
            rule: rule,
            id: dimensionId,
            name: value,
            value: '',
            error: '',
          });

          dimensions.push({
            rule: rule,
            id: dimensionId,
            name: value,
            value: '',
            error: '',
          });
        }
      }
      this.dimensions.sort((a: dynamicObj, b: dynamicObj) => a.id - b.id);
      dimensions.sort((a: dynamicObj, b: dynamicObj) => a.id - b.id);
      return dimensions;
    },

    generateM3DimensionDropdowns() {
      try {
        for (let [account_number, dimensions_obj] of Object.entries(
          this.dimension_rules,
        )) {
          const dimensions = dimensions_obj as dynamicObj;
          for (let [dimenion_key, ranges] of Object.entries(dimensions)) {
            const dimenion_options = this.generateM3CombinationByRanges(ranges as dynamicObj[]);
            if (dimenion_options.length === 0) {
              continue;
            }

            if (!this.dimension_dropdowns.hasOwnProperty(account_number)) {
              this.dimension_dropdowns[account_number] = {} as dynamicObj;
            }
            this.dimension_dropdowns[account_number][dimenion_key] =
              dimenion_options;
          }
        }
      } catch (error) {
        throw error;
      }
    },

    getM3RangeKey(range: dynamicObj) {
      return (
        range.start.toUpperCase().replace(/\s+/g, '_') +
        '_' +
        range.end.toUpperCase().replace(/\s+/g, '_')
      );
    },

    generateM3CombinationByRanges(ranges: dynamicObj[]) {
      let combinations = [] as dynamicObj[];
      try {
        for (const range of ranges) {
          const key = this.getM3RangeKey(range);

          if (!this.combination_by_range.hasOwnProperty(key)) {
            this.combination_by_range[key] = this.generateM3Combinations(range);
          }

          combinations.push(...this.combination_by_range[key]);
          if (combinations.length > this.max_combinations) {
            throw new Error('Combination over load');
          }
        }
      } catch (error) {
        combinations = [];
        // logInfo('Combination over load')
      }
      return combinations;
    },

    generateM3Combinations(range: dynamicObj): dynamicObj[] {
      let combinations: dynamicObj[] = [];
      try {
        const start: string = range.start;
        const end: string = range.end;

        if (start.length !== end.length) {
          throw new Error('Combination over load');
        }

        const parts: string[][] = [];

        for (let i = 0; i < start.length; i++) {
          if (start[i] === end[i]) {
            parts.push([start[i]]);
          } else {
            const startCode: number = start.charCodeAt(i);
            const endCode: number = end.charCodeAt(i);
            const rangePart: string[] = [];

            if (startCode > endCode) {
              // Alphabetic range W-C , from W to Z and then from A to C
              for (let code = startCode; code <= 'Z'.charCodeAt(0); code++) {
                rangePart.push(String.fromCharCode(code));
              }
              for (let code = 'A'.charCodeAt(0); code <= endCode; code++) {
                rangePart.push(String.fromCharCode(code));
              }
            } else {
              for (let code = startCode; code <= endCode; code++) {
                rangePart.push(String.fromCharCode(code));
              }
            }

            parts.push(rangePart);
          }
        }

        this.combineM3('', parts, 0, combinations);
        return combinations;
      } catch (error) {
        throw new Error('Combination over load');
      }
    },

    combineM3(
      prefix: string,
      parts: string[][],
      index: number,
      combinations: dynamicObj[],
    ): void {
      if (combinations.length > this.max_combinations) {
        throw new Error('Combination over load');
      }
      if (index === parts.length) {
        combinations.push({
          value: prefix,
          label: prefix,
        });
        return;
      }

      for (let char of parts[index]) {
        this.combineM3(prefix + char, parts, index + 1, combinations);
      }
    },

    async prepareM3DimensionRulesData(dimensionsData: any) {
      this.dimension_rules = {} as dynamicObj;

      if (dimensionsData.length === 0) {
        deleteObjectFromIndexDb(
          await getOrgBasedKey(
            this.accounting_type_store.getSettingsName(
              auto_accounting_dimensions_options_setting,
            ),
          ),
        );
        return;
      }

      this.prepareM3DimensionRulesDropdown(dimensionsData);

      saveObjectToIndexDbWithExpiry(
        await getOrgBasedKey(
          this.accounting_type_store.getSettingsName(
            auto_accounting_dimensions_options_setting,
          ),
        ),
        dimensionsData,
        true,
      );
    },

    prepareM3DimensionRulesDropdown(dimensionsData: any) {
      for (const [index, row] of dimensionsData.entries()) {
        const data = {} as dynamicObj;

        Object.keys(row).forEach((key) => {
          // Check if the key ends with a space
          let newKey = key.trim().toLowerCase().replace(/\s+/g, '_');
          data[newKey] = row[key];
        });

        const account_name = data['accounting_identity'];
        const accounting_dimension = data['accounting_dimension'];
        const to_accounting_identity = data['to_accounting_identity'];
        const from_accounting_identity = data['from_accounting_identity'];
        if (!account_name || !data['accounting_dimension']) {
          continue;
        }

        if (!this.dimension_rules.hasOwnProperty(account_name)) {
          this.dimension_rules[account_name] = {} as dynamicObj;
        }

        if (
          !this.dimension_rules[account_name].hasOwnProperty(
            accounting_dimension,
          )
        ) {
          this.dimension_rules[account_name][accounting_dimension] =
            [] as dynamicObj[];
        }

        this.dimension_rules[account_name][accounting_dimension].push({
          end: to_accounting_identity as string,
          start: from_accounting_identity as string,
        });
      }
    },

    async prepareM3DimensionsOptionsData(dimensionsData: any) {
      this.dimension_dropdowns = {} as dynamicObj;

      if (!dimensionsData || dimensionsData.length == 0) {
        deleteObjectFromIndexDb(
          await getOrgBasedKey(
            this.accounting_type_store.getSettingsName(
              dimension_general_dropdown_accounting,
            ),
          ),
        );
        return;
      }

      this.prepareM3DimensionsOptionsDropdown(dimensionsData);

      saveObjectToIndexDbWithExpiry(
        await getOrgBasedKey(
          this.accounting_type_store.getSettingsName(
            dimension_general_dropdown_accounting,
          ),
        ),
        dimensionsData,
        true,
      );
    },

    prepareM3DimensionsOptionsDropdown(dimensionsData: any) {
      const data_by_dimensionid = {} as dynamicObj;

      Array.from(dimensionsData).forEach((row: any, index: any) => {
        const dimensionid = row.dimension;
        if (!dimensionid || !row.description) {
          return;
        }
        if (!data_by_dimensionid.hasOwnProperty(dimensionid)) {
          data_by_dimensionid[dimensionid] = [] as dynamicObj[];
        }

        let label = '';
        if (
          row.hasOwnProperty('codevalue') &&
          !isUndefinedOrNull(row.codevalue) &&
          !isEmptyString(row.codevalue)
        ) {
          label += row.codevalue + ' - ';
        }
        label += row.description;

        data_by_dimensionid[dimensionid].push({
          value: row.id,
          label: label,
        });
      });

      let dimension_dropdowns = {} as dynamicObj;
      for (const key in data_by_dimensionid) {
        const sortedArray = data_by_dimensionid[key].sort((a: any, b: any) => {
          return a.label.localeCompare(b.label) as number;
        });
        dimension_dropdowns[String(key)] = [...sortedArray];
      }
      this.dimension_general_dropdowns = dimension_dropdowns;
    },

    // first fill the data from cache until requests are completed
    async setM3DataFromCache() {
      let isDataExpired = false;

      const dimension_rules_data = await getObjectFromIndexDbWithExpiry(await getOrgBasedKey(this.accounting_type_store.getSettingsName(auto_accounting_dimensions_options_setting)), true, true);
      if (dimension_rules_data != null && Object.keys(dimension_rules_data).length > 0) {
        this.prepareM3DimensionRulesDropdown(dimension_rules_data.value);

        if (dimension_rules_data.isDataExpired) {
          isDataExpired = true;
        }
      }

      const accSettingName = this.hasM3PoMatchData() ? auto_accounting_po_acc_list_name_setting : auto_accounting_acc_list_name_setting;

      const acc_list = await getObjectFromIndexDbWithExpiry(await getOrgBasedKey(this.accounting_type_store.getSettingsName(accSettingName)), true, true);
      if (acc_list != null && Object.keys(acc_list).length > 0) {
        this.prepareM3AccountsAndDimensions(acc_list.value);

        if (acc_list.isDataExpired) {
          isDataExpired = true;
        }
      } else if (!isDataExpired) {
        // Active acc-list key has no cache and data isn't expired — load it from
        // the server (only when the adjacent key is cached; guarded inside).
        await this.loadM3AccountsFromServer(accSettingName);
      }

      const general_dropdown = await getObjectFromIndexDbWithExpiry(await getOrgBasedKey(this.accounting_type_store.getSettingsName(dimension_general_dropdown_accounting)), true, true);
      if (general_dropdown != null && Object.keys(general_dropdown).length > 0) {
        this.prepareM3DimensionsOptionsDropdown(general_dropdown.value);

        if (general_dropdown.isDataExpired) {
          isDataExpired = true;
        }
      }

      if (this.accounts != null && this.accounts.length > 0) {
        this.accounts_loaded = true;
      }

      return isDataExpired;
    },

    async getCacheData(key: string) {

      return await getObjectFromIndexDbWithExpiry(
        await getOrgBasedKey(key),
        true,
        true,
      );
    },

    async getCacheDataValues(key: string) {
      let response = null;
      const data = await this.getCacheData(key);
      if (data != null && Object.keys(data).length > 0) {
        response = data.value;
      }
       
      return response;
    },

    // hasLineItemChanges(newData: any, savedData: any) {
    //   const currentFieldMap = new Map(
    //     // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    //     newData.map((item: any) => [ item.net_amount.orignal_name, item.net_amount.formatted_value ])
    //   );

    //   const lastFieldMap = new Map();

    //   savedData.forEach((item: any) => {
    //     if(item.type != 'total_line_item') {
    //       return;
    //     }
    //     // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    //     const netField = item.fields?.find((f: any) => f.startsWith('net_amount'));
    //     if (netField) {
    //       lastFieldMap.set(netField, item.amount);
    //     }
    //   });

    //   // 1. Check for updated values or missing fields in savedData
    //   for (const [field, value] of currentFieldMap.entries()) {
    //     if (!lastFieldMap.has(field)) {
    //       return true; // New entry added
    //     }
    //     if (lastFieldMap.get(field) !== value) {
    //       return true; // Value updated
    //     }
    //   }

    //   // 2. Check for deleted rows
    //   for (const field of lastFieldMap.keys()) {
    //     if (!currentFieldMap.has(field)) {
    //       return true; // Entry removed
    //     }
    //   }

    //   return false; // No changes detected
    // }
  },
});
